/**
 * PRSVR // ENTERPRISE ESTATE — Holding Company Homepage Matrix
 *
 * Brutalist monochrome grid showcasing all four PRSVR pillars:
 *   01 ARCHIVE  — fashion / live inventory
 *   02 SPORTS   — institutional sports infrastructure
 *   03 ACADEMY  — scholar-athlete K-12 cohort
 *   04 LAB      — Provenance Protocol (B2B autonomous systems)
 *
 * Pure #000 on #FFF. 1px hard borders everywhere. Responsive: mobile = stacked,
 * tablet+ = razor-sharp grid. Designed to feel like a corporate prospectus,
 * not a marketing site.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const DILA_HAT_VIDEO = '/media/dila-hat.mp4';

// Shopify collections are fetched live and can be briefly slow on a cold start.
// Retry a few times before surfacing an empty/"restocking" state to the visitor.
const fetchCollection = async (handle, tries = 3) => {
  for (let i = 0; i < tries; i++) {
    try {
      const r = await api.get(`/shopify/collections/${handle}`);
      const list = r.data?.collection?.products || r.data?.products || [];
      if (list.length) return list;
    } catch (e) { /* fallthrough to retry */ }
    await new Promise((res) => setTimeout(res, 600 * (i + 1)));
  }
  return [];
};

const Section00_ChilaPost = () => {
  const [img, setImg] = useState(null);
  const [thumbs, setThumbs] = useState([]);

  useEffect(() => {
    fetchCollection('worn-by-ricco-ross')
      .then((list) => {
        const score = (u = '') => {
          const s = u.toLowerCase();
          if (/riccoross|summa/.test(s)) return 6;
          if (/family/.test(s)) return 5;
          if (/savant/.test(s)) return 4;
          if (/prsvr[_-]?\d/.test(s)) return 3;
          return 0;
        };
        const imgs = list.map((p) => p.image).filter(Boolean).sort((a, b) => score(b) - score(a));
        setImg(imgs[0] || null);
        setThumbs(list.slice(0, 3).map((p) => ({ img: p.image, name: p.name })).filter((t) => t.img));
      })
      .catch(() => {});
  }, []);

  return (
    <section className="border-b border-black" data-testid="section-chila-post">
      <div className="grid lg:grid-cols-2 border-b border-black">
        {/* Left — Ricco editorial frame */}
        <div className="aspect-square bg-black lg:border-r border-black relative overflow-hidden">
          {img && (
            <img src={img} alt="D.I.L.A. — The Chila Edit · Worn by Ricco Ross" className="absolute inset-0 w-full h-full object-cover" />
          )}
          <div className="absolute top-4 left-4 bg-white text-black px-3 py-1.5 text-[9px] font-black tracking-[0.35em] uppercase">
            New Editorial
          </div>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <div className="text-[9px] tracking-[0.35em] uppercase font-black">Worn By Ricco Ross</div>
            <div className="text-xs font-mono mt-1 opacity-80">'Beauty in Black' · Codified as CHILA</div>
          </div>
        </div>

        {/* Right — Post body */}
        <div className="bg-white p-8 lg:p-12 flex flex-col justify-between">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-black pb-3 mb-6">
              The Chila Edit · Worn By Ricco Ross
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] tracking-tight mb-6">
              D.I.L.A. <span className="opacity-40">//</span><br />The Chila Edit
            </h2>
            <p className="text-sm leading-relaxed max-w-md mb-6">
              A brand isn't defined by the garments it produces; it's validated by the individuals who inhabit them.
              Ricco Ross — anchoring screens as Horace in Tyler Perry's <em>'Beauty in Black'</em> — didn't just wear
              the pieces. He claimed the narrative, codifying his journey as <strong>CHILA</strong>. Shop the exact
              silhouettes, heavy custom-washed French terries, and structural layers he selected.
            </p>

            {thumbs.length > 0 && (
              <div className="grid grid-cols-3 gap-px bg-black border border-black max-w-md mb-2" data-testid="chila-post-thumbs">
                {thumbs.map((t, i) => (
                  <Link key={i} to="/chila" className="bg-white aspect-square overflow-hidden group">
                    <img src={t.img} alt={t.name} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/chila"
            className="mt-8 inline-flex items-center justify-between border border-black bg-black text-white px-6 py-5 hover:bg-white hover:text-black transition-colors group"
            data-testid="cta-chila-post"
          >
            <span className="text-xs font-black tracking-[0.25em] uppercase">Enter The Chila Edit</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Section_HatDetails = () => (
  <section className="border-b border-black" data-testid="section-hat-details">
    <div className="grid lg:grid-cols-2 border-b border-black">
      {/* Left — Autoplay portrait detail reel */}
      <div className="bg-black lg:border-r border-black relative overflow-hidden flex items-center justify-center min-h-[420px] lg:min-h-[560px]">
        <video
          src={DILA_HAT_VIDEO}
          poster="/media/dila-hat-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="max-h-[520px] w-auto h-auto object-contain"
          data-testid="hat-details-video"
        />
        <div className="absolute top-4 left-4 bg-white text-black px-3 py-1.5 text-[9px] font-black tracking-[0.35em] uppercase">
          Newest Entry
        </div>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="text-[9px] tracking-[0.35em] uppercase font-black">D.I.L.A. · Reverse-Seam Bucket Hat</div>
          <div className="text-xs font-mono mt-1 opacity-80">Sun-Faded · Hand-Finished · Small-Batch</div>
        </div>
      </div>

      {/* Right — Copy + CTA */}
      <div className="bg-white p-8 lg:p-12 flex flex-col justify-between">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-black pb-3 mb-6">
            D.I.L.A. · In The Details
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] tracking-tight mb-6">
            Explore The Intricate Details Of DILA.
          </h2>
          <p className="text-sm leading-relaxed max-w-md mb-6">
            No two washes cure alike. Every seam is reversed, every fade is earned in the dye bath.
            This is texture you can only understand up close — the byproduct of disciplined, small-batch craft.
          </p>
        </div>

        <Link
          to="/dila"
          className="mt-8 inline-flex items-center justify-between border border-black bg-black text-white px-6 py-5 hover:bg-white hover:text-black transition-colors group"
          data-testid="cta-get-buckets"
        >
          <span className="text-xs font-black tracking-[0.25em] uppercase">Get Buckets</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  </section>
);

const Section01_Rotation = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollection('dila-1')
      .then((list) => setProducts(list.slice(0, 5)))
      .finally(() => setLoading(false));
  }, []);

  return (
  <section className="border-b border-black" data-testid="section-rotation">
    <div className="grid lg:grid-cols-2 border-b border-black">
      {/* Left — Live shot viewport */}
      <div className="aspect-square bg-black lg:border-r border-black relative overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_0173a475-1e01-4336-ba9e-8f090ef02c6f/artifacts/utqeauqe_HERO.PNG"
          alt="What We Wore to Coca Cola Nationals — DILA"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-4 left-4 bg-white text-black px-3 py-1.5 text-[9px] font-black tracking-[0.35em] uppercase flex items-center gap-2">
          <span className="relative inline-flex w-2.5 h-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          LIVE
        </div>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="text-[9px] tracking-[0.35em] uppercase font-black">PHOTOGRAPHED · COCA COLA NATIONALS</div>
          <div className="text-xs font-mono mt-1 opacity-80">DESIGNED BY DILA // SMALL-BATCH DROP</div>
        </div>
      </div>

      {/* Right — Token list */}
      <div className="bg-white p-8 lg:p-12 flex flex-col justify-between">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-black pb-3 mb-6">
            WHAT WE WORE TO COCA COLA NATIONALS
          </div>
          <ul className="space-y-0 border-t border-black" data-testid="dila-token-list">
            {loading && (
              <li className="py-4 flex items-center gap-3 border-b border-black text-xs">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading live inventory…
              </li>
            )}
            {!loading && products.length === 0 && (
              <li className="py-4 border-b border-black text-xs italic opacity-60">
                Collection currently restocking.
              </li>
            )}
            {!loading && products.map((p, i) => {
              const variant = p.variants?.[0] || {};
              const available = p.available ?? variant.available ?? true;
              const status = available ? 'AVAILABLE' : 'ALLOCATED';
              const statusType = available ? 'ok' : 'alert';
              return (
                <li key={p.id || p.handle || i} className="border-b border-black">
                  <Link
                    to={`/dila/${p.handle}`}
                    className="grid grid-cols-[auto,1fr,auto] gap-4 py-4 items-center hover:bg-black/5 px-1 -mx-1 transition-colors"
                    data-testid={`dila-token-${i}`}
                  >
                    <span className="font-mono text-xs">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-sm font-bold uppercase tracking-tight">{p.title || p.name}</span>
                    <span className={`text-[10px] font-mono tracking-[0.2em] uppercase border border-black px-2 py-1 ${statusType === 'ok' ? '' : 'opacity-100'}`}>
                      {status}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
        <Link
          to="/dila"
          className="mt-8 inline-flex items-center justify-between border border-black bg-black text-white px-6 py-5 hover:bg-white hover:text-black transition-colors group"
          data-testid="cta-enter-archive"
        >
          <span className="text-xs font-black tracking-[0.25em] uppercase">
            Enter D.I.L.A. Design House
          </span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>

        {/* DILA — About the Project (collapsable) */}
        <details className="mt-8 border-t border-black pt-6 group" data-testid="dila-about">
          <summary className="cursor-pointer flex items-center justify-between text-[10px] font-black tracking-[0.35em] uppercase list-none">
            <span>About D.I.L.A. — The Project</span>
            <span className="text-xl group-open:rotate-45 transition-transform leading-none">+</span>
          </summary>
          <div className="mt-5 space-y-4 text-sm leading-relaxed">
            <p><em>Designed by Brandon Williamson with the help of A.I. Agents that assist in hyper-accelerating his operational bandwidth so he can stay in his creative flow state.</em></p>
            <p>Here at D.I.L.A., we don't participate in the overproduction cycle that is currently suffocating the apparel industry. We don't build massive, faceless warehouses full of generic inventory waiting for a markdown season.</p>
            <p><strong>Every single drop from DILA is engineered in highly disciplined, small-batch runs.</strong></p>
            <p>Operating this way allows us to keep our environmental footprint exceptionally tight — we only pull raw textiles and mill yarn for the exact allocations we intend to ship. There is zero excess deadstock, zero landfill waste, and zero cutting-room compromise.</p>
            <p>The immediate byproduct of this operational discipline is total exclusivity for your wardrobe. When you are on the floor or out in the city, you are wearing an asset that was constructed with human intent, not industrial gluttony. Once a batch is cleared from the manifest, the formula resets — preventing exact replicas.</p>
            <p className="font-black tracking-[0.2em] uppercase text-xs pt-2 border-t border-black/20">
              Conscious infrastructure. Elite rarity. From Napkin to Next Fit. Designed by DILA.
            </p>
          </div>
        </details>
      </div>
    </div>
  </section>
  );
};

const ProductToken = ({ index, name, status, statusType }) => {
  const dotStyle = statusType === 'ok' ? '' : statusType === 'warn' ? 'opacity-50' : 'opacity-100';
  return (
    <li className="grid grid-cols-[auto,1fr,auto] gap-4 border-b border-black py-4 items-center">
      <span className="font-mono text-xs">{index}</span>
      <span className="text-sm font-bold uppercase tracking-tight">{name}</span>
      <span className={`text-[10px] font-mono tracking-[0.2em] uppercase border border-black px-2 py-1 ${dotStyle}`}>
        {status}
      </span>
    </li>
  );
};

const Section02_Sports = () => (
  <section className="bg-black text-white border-b border-black" data-testid="section-sports">
    <div className="p-8 lg:p-12">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-white/30 pb-3 mb-6">
            02 // ATHLETIC MATRIX
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] tracking-tight mb-6">
            PRSVR SPORTS<br />
            <span className="opacity-50">// AUTOMATED</span><br />
            <span className="opacity-50">TEAM LOCKERS</span>
          </h2>
          <p className="text-sm leading-relaxed text-white/70 max-w-md mb-8">
            A multi-sport operational matrix engineered to convert grassroots teams into
            fully-stocked, fully-branded organizations. Custom merch hubs, drop-ship
            apparel, and live order routing across Basketball, Soccer, Volleyball, and 7×7.
          </p>
          <div className="grid grid-cols-2 gap-px bg-white/20 border border-white/20 max-w-md">
            {['BASKETBALL', 'SOCCER', 'VOLLEYBALL', '7×7 FOOTBALL'].map((s) => (
              <div key={s} className="bg-black px-4 py-3 text-[10px] font-black tracking-[0.25em] uppercase">
                {s}
              </div>
            ))}
          </div>
        </div>

        <div className="border border-white/30 p-6 lg:p-8 flex flex-col justify-between">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-black opacity-60 mb-3">
              Active Portal
            </div>
            <h3 className="text-2xl font-black uppercase tracking-tight">BYWE</h3>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-60 mt-1">
              Grassroots Team Merch Hub
            </div>
            <p className="mt-4 text-xs text-white/60 leading-relaxed">
              Eight live products. Player-name reservations. Price-tamper protection.
              Coach-routed order notifications via Resend.
            </p>
          </div>
          <Link
            to="/team/bywe"
            className="mt-8 inline-flex items-center justify-between border border-white px-5 py-4 hover:bg-white hover:text-black transition-colors"
            data-testid="cta-bywe-hub"
          >
            <span className="text-[11px] font-black tracking-[0.25em] uppercase">
              Enter BYWE Hub
            </span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <Link
        to="/uniform-order"
        className="mt-10 inline-flex items-center gap-3 border border-white px-6 py-5 hover:bg-white hover:text-black transition-colors"
        data-testid="cta-sports-overview"
      >
        <span className="text-xs font-black tracking-[0.25em] uppercase">
          Full Sports Matrix
        </span>
        <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

const Section03_Academy = () => (
  <section className="border-b border-black" data-testid="section-academy">
    <div className="grid lg:grid-cols-[1.2fr,1fr]">
      <div className="p-8 lg:p-12 lg:border-r border-black">
        <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-black pb-3 mb-6">
          03 // ACADEMY LAYER
        </div>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] tracking-tight mb-6">
          SCHOLAR ATHLETE ACADEMY<br />
          <span className="opacity-50">// FALL 2026</span><br />
          <span className="opacity-50">IN-PERSON COHORT</span>
        </h2>
        <p className="text-sm leading-relaxed max-w-xl mb-4">
          An honors curriculum track anchored by{' '}
          <strong>OpenStax academic text registries</strong> and aligned with the{' '}
          <strong>California Scholastic Standards</strong>. Daily 3-hour instructional
          blocks paired with athletic conditioning, AI-augmented application exercises,
          and the L'Chaim 350-day discipline journey.
        </p>
        <ul className="space-y-2 mt-6 mb-8">
          {[
            'OpenStax + CA Standards-aligned curriculum',
            'AI-coached daily application exercises',
            'Embedded CIF / high-school eligibility tracking',
            'L\u2019Chaim discipline & character framework',
          ].map((t) => (
            <li key={t} className="flex gap-3 items-start text-xs">
              <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="font-medium">{t}</span>
            </li>
          ))}
        </ul>
        <Link
          to="/academy"
          className="inline-flex items-center justify-between border border-black bg-black text-white px-6 py-5 hover:bg-white hover:text-black transition-colors group"
          data-testid="cta-academy"
        >
          <span className="text-xs font-black tracking-[0.25em] uppercase">
            Access Education Portal // Enrollment Dispatch
          </span>
          <ArrowRight className="w-4 h-4 ml-3 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      <div className="p-8 lg:p-12 bg-black text-white flex flex-col justify-between">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-white/30 pb-3 mb-6">
            COHORT METRICS
          </div>
          <div className="space-y-5">
            <MetricRow label="Curriculum Standards" value="CA + OpenStax" />
            <MetricRow label="Daily Instructional Hours" value="3.0" />
            <MetricRow label="Sport Tracks" value="04" />
            <MetricRow label="Discipline Modules" value="350 DAYS" />
            <MetricRow label="CIF Eligibility Tracking" value="ELITE" />
          </div>
        </div>
        <div className="mt-10 text-[10px] tracking-[0.25em] uppercase opacity-60 leading-relaxed">
          Founded by Brandon C. Williamson<br />
          Headquartered · California
        </div>
      </div>
    </div>
  </section>
);

const MetricRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between border-b border-white/15 pb-3">
    <span className="text-[10px] tracking-[0.3em] uppercase opacity-60 font-bold">{label}</span>
    <span className="font-mono font-black text-sm">{value}</span>
  </div>
);

const NODES = [
  { id: '01', label: 'Natural Language Sketch', sub: 'AI Sandbox Prompting' },
  { id: '02', label: 'Disconnected Code Dispatch', sub: 'FastAPI + GitHub Release Control' },
  { id: '03', label: 'Manufacturing Logistics', sub: 'Programmatic Apparel Output' },
  { id: '04', label: 'Doorstep Reception', sub: 'Finished Asset / Editorial Photoshoot' },
];

const Section04_Lab = () => {
  const [form, setForm] = useState({ name: '', email: '', requirements: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.requirements.trim()) {
      toast.error('All three fields required.');
      return;
    }
    setSending(true);
    try {
      // Reuse the existing client-brief endpoint if present, else fall back to /contact
      await api.post('/lab/inquiry', form).catch(() =>
        api.post('/contact', { ...form, source: 'lab-inquiry' })
      );
      setSent(true);
      toast.success('Inquiry received. PRSVR Lab will respond within 48h.');
    } catch (_) {
      toast.error('Submission failed. Please retry.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="border-b border-black" data-testid="section-lab">
      <div className="p-8 lg:p-12 grid lg:grid-cols-[1fr,1fr] gap-10 lg:gap-16">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-black pb-3 mb-6">
            04 // SOLUTIONS LAB
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.95] tracking-tight mb-3">
            THE PROVENANCE<br />PROTOCOL
          </h2>
          <p className="text-sm leading-relaxed mb-8 max-w-md">
            A vertically-integrated autonomous pipeline. From napkin sketch to doorstep
            delivery — no human handoffs, no agency lag.
          </p>

          {/* Vertical pipeline */}
          <ol className="space-y-0 border-t border-black" data-testid="pipeline">
            {NODES.map((n, i) => (
              <li key={n.id} className="border-b border-black grid grid-cols-[auto,1fr] gap-5 py-5 items-start">
                <span className="font-mono text-xs font-black border border-black px-2 py-1">
                  {n.id}
                </span>
                <div>
                  <div className="text-sm font-black uppercase tracking-tight">{n.label}</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase opacity-60 mt-1">{n.sub}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Inquiry Form */}
        <div className="bg-black text-white p-6 lg:p-8 self-start">
          <div className="text-[10px] tracking-[0.35em] uppercase font-black border-b border-white/30 pb-3 mb-6">
            Initiate An Autonomous System Inquiry
          </div>
          {sent ? (
            <div className="py-12 text-center" data-testid="lab-inquiry-sent">
              <div className="w-12 h-12 border-2 border-white mx-auto flex items-center justify-center mb-5">
                <Check className="w-6 h-6" />
              </div>
              <div className="text-xs tracking-[0.3em] uppercase opacity-80">
                Inquiry received
              </div>
              <p className="mt-3 text-sm opacity-60">
                PRSVR Lab responds within 48 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="lab-inquiry-form">
              <FormField label="Client Name" name="name" value={form.name}
                onChange={(v) => setForm({ ...form, name: v })} />
              <FormField label="Organization Email" name="email" type="email" value={form.email}
                onChange={(v) => setForm({ ...form, email: v })} />
              <FormField label="System Requirements" name="requirements"
                multiline value={form.requirements}
                onChange={(v) => setForm({ ...form, requirements: v })} />
              <button
                type="submit"
                disabled={sending}
                className="w-full mt-2 border border-white py-4 font-black text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="lab-submit"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Dispatch Inquiry
              </button>
            </form>
          )}
          <Link
            to="/lab"
            className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase opacity-60 hover:opacity-100"
            data-testid="cta-lab-more"
          >
            Full Protocol Brief <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const FormField = ({ label, name, value, onChange, type = 'text', multiline = false }) => {
  const baseCls =
    'w-full bg-black border border-white/30 px-3 py-3 text-sm text-white font-mono focus:border-white outline-none transition-colors placeholder-white/30';
  return (
    <div>
      <label className="block text-[9px] font-black tracking-[0.3em] uppercase opacity-60 mb-1.5">
        {label}
      </label>
      {multiline ? (
        <textarea
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className={baseCls}
          data-testid={`lab-field-${name}`}
          required
        />
      ) : (
        <input
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={baseCls}
          data-testid={`lab-field-${name}`}
          required
        />
      )}
    </div>
  );
};

const Matrix = () => {
  return (
    <div className="min-h-screen bg-white text-black" data-testid="prsvr-matrix">
      {/* ============ NAV CANOPY ============ */}
      <nav className="sticky top-0 z-40 bg-white border-b border-black" data-testid="nav-canopy">
        <div className="grid grid-cols-[1fr,auto] items-center px-5 lg:px-8 h-14">
          <Link to="/matrix" className="font-black text-xs sm:text-sm tracking-[0.2em] uppercase">
            PRSVR <span className="opacity-50">//</span> ENTERPRISE ESTATE
          </Link>
          <ul className="hidden sm:flex gap-1 items-center">
            <NavItem index="01" label="ARCHIVE" to="/heritage" />
            <NavItem index="02" label="SPORTS" to="/sports" />
            <NavItem index="03" label="ACADEMY" to="/saa" />
            <NavItem index="04" label="LAB" to="https://brandoncwilliamsonllc.com" external />
          </ul>
        </div>
      </nav>

      {/* ============ HERO LINE ============ */}
      <section className="border-b border-black px-5 lg:px-8 py-16 lg:py-20" data-testid="section-hero">
        <div className="text-[10px] tracking-[0.35em] uppercase font-black mb-6">
          PRSVR // HOLDING COMPANY · EST. 2024
        </div>
        <h1 className="text-5xl sm:text-7xl lg:text-[9rem] font-black uppercase leading-[0.85] tracking-tight">
          A VERTICALLY<br />INTEGRATED<br />
          <span className="opacity-30">ESTATE OF</span><br />
          <span className="opacity-30">DISCIPLINES.</span>
        </h1>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-black border border-black max-w-3xl">
          {[
            { k: 'APPAREL', v: 'PRSVR', to: '/heritage', external: false },
            { k: 'SPORTS', v: 'Team Apparel', to: '/uniform-order', external: false },
            { k: 'ACADEMY', v: 'Scholar Athlete Academy', to: '/academy', external: false },
            { k: 'CONTACT', v: "Let's Build", to: 'https://brandoncwilliamsonllc.com', external: true },
          ].map((c) => {
            const inner = (
              <div className="bg-white px-4 py-4 hover:bg-black hover:text-white transition-colors h-full" data-testid={`hero-tile-${c.k.toLowerCase()}`}>
                <div className="text-[9px] tracking-[0.3em] uppercase font-black">{c.k}</div>
                <div className="text-base font-bold mt-1">{c.v}</div>
              </div>
            );
            return c.external ? (
              <a key={c.k} href={c.to} target="_blank" rel="noreferrer" className="block">{inner}</a>
            ) : (
              <Link key={c.k} to={c.to} className="block">{inner}</Link>
            );
          })}
        </div>
      </section>

      {/* Newest Entry — DILA hat detail reel */}
      <Section_HatDetails />

      {/* Live Command Center — Road to Michigan tracker (TOP ENTRY) */}
      <Link
        to="/road-to-michigan"
        className="group block border-b border-black bg-black text-white hover:bg-white hover:text-black transition-colors"
        data-testid="cta-run-tracker"
      >
        <div className="px-5 lg:px-8 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="flex items-center gap-4">
            <span className="relative inline-flex w-3 h-3 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
            </span>
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase font-black opacity-60">Live Command Center · Tracking Now</div>
              <div className="text-xl sm:text-3xl font-black uppercase tracking-tight">The DILA Road To Michigan</div>
              <div className="text-[11px] tracking-[0.15em] uppercase font-bold text-green-400 mt-1">Follow B2 &amp; Brave · Get Buckets → Michigan</div>
            </div>
          </div>
          <div className="flex items-center justify-between md:justify-end gap-5">
            <img
              src="https://customer-assets.emergentagent.com/job_9a211650-b78b-41f7-9969-b52217e0c686/artifacts/t8ofsmmw_IMG_0070.jpeg"
              alt="The DILA Road to Michigan live tracker"
              className="block w-40 sm:w-56 md:w-auto md:h-32 h-auto object-contain rounded-lg ring-2 ring-[#C9A961]/60 shadow-[0_10px_30px_rgba(201,169,97,0.35)] rotate-[-2deg] group-hover:rotate-0 group-hover:scale-105 transition-transform duration-300"
            />
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform shrink-0" />
          </div>
        </div>
      </Link>

      <Section00_ChilaPost />
      <Section01_Rotation />
      <Section02_Sports />
      <Section03_Academy />
      <Section04_Lab />

      {/* Footer */}
      <footer className="px-5 lg:px-8 py-10 text-[10px] tracking-[0.3em] uppercase flex flex-col sm:flex-row justify-between gap-2">
        <span>© 2026 PRSVR Holdings // All Vectors Reserved</span>
        <span className="opacity-60">prsvr.store · scholarathlete.academy</span>
      </footer>
    </div>
  );
};

const NavItem = ({ index, label, to, external }) => {
  const cls = "px-3 py-1.5 flex items-center gap-1.5 text-[10px] font-black tracking-[0.25em] uppercase hover:bg-black hover:text-white transition-colors";
  const inner = (
    <>
      <span className="opacity-50 font-mono">[{index}]</span>
      <span>{label}</span>
    </>
  );
  return (
    <li>
      {external ? (
        <a href={to} target="_blank" rel="noreferrer" className={cls} data-testid={`nav-${label.toLowerCase()}`}>{inner}</a>
      ) : (
        <Link to={to} className={cls} data-testid={`nav-${label.toLowerCase()}`}>{inner}</Link>
      )}
    </li>
  );
};

export default Matrix;
